// Big-endian binary reader for PSD parsing.
//
// PSD is big-endian throughout, so every multi-byte read here defaults to BE.
// This class is intentionally minimal — no DOM, no Buffer, no streams; just
// a Uint8Array and a cursor.  Out-of-bounds reads throw with a clean message
// so callers can wrap-and-rethrow with section context.

export class BinaryReaderError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BinaryReaderError";
  }
}

export class BinaryReader {
  private readonly view: DataView;
  private readonly bytes: Uint8Array;
  private offset: number;

  constructor(bytes: Uint8Array, offset = 0) {
    this.bytes = bytes;
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    this.offset = offset;
  }

  // -- Cursor management ------------------------------------------------

  tell(): number {
    return this.offset;
  }

  seek(offset: number): void {
    if (offset < 0 || offset > this.bytes.byteLength) {
      throw new BinaryReaderError(
        `seek out of bounds: offset=${offset}, length=${this.bytes.byteLength}`,
      );
    }
    this.offset = offset;
  }

  skip(n: number): void {
    this.ensure(n);
    this.offset += n;
  }

  eof(): boolean {
    return this.offset >= this.bytes.byteLength;
  }

  remaining(): number {
    return this.bytes.byteLength - this.offset;
  }

  private ensure(n: number): void {
    if (n < 0) {
      throw new BinaryReaderError(`cannot read negative length ${n}`);
    }
    if (this.offset + n > this.bytes.byteLength) {
      throw new BinaryReaderError(
        `read past end: need ${n} bytes at offset ${this.offset}, ` +
          `but buffer length is ${this.bytes.byteLength}`,
      );
    }
  }

  // -- Integers (all big-endian) ----------------------------------------

  readU8(): number {
    this.ensure(1);
    const v = this.view.getUint8(this.offset);
    this.offset += 1;
    return v;
  }

  readI8(): number {
    this.ensure(1);
    const v = this.view.getInt8(this.offset);
    this.offset += 1;
    return v;
  }

  readU16(): number {
    this.ensure(2);
    const v = this.view.getUint16(this.offset, false);
    this.offset += 2;
    return v;
  }

  readI16(): number {
    this.ensure(2);
    const v = this.view.getInt16(this.offset, false);
    this.offset += 2;
    return v;
  }

  readU32(): number {
    this.ensure(4);
    const v = this.view.getUint32(this.offset, false);
    this.offset += 4;
    return v;
  }

  readI32(): number {
    this.ensure(4);
    const v = this.view.getInt32(this.offset, false);
    this.offset += 4;
    return v;
  }

  /**
   * 64-bit unsigned integer (big-endian), returned as a JS `number`.
   * Throws if the value exceeds `Number.MAX_SAFE_INTEGER` — for PSB section
   * lengths this is fine in practice (max ~9 PB document) and avoids dragging
   * in BigInt for every length read.
   */
  readU64(): number {
    this.ensure(8);
    const hi = this.view.getUint32(this.offset, false);
    const lo = this.view.getUint32(this.offset + 4, false);
    this.offset += 8;
    // 2^53 - 1 = 0x1F_FFFF_FFFF_FFFF, i.e. hi ≤ 0x1F_FFFF and (hi,lo) fits.
    if (hi > 0x001f_ffff) {
      throw new BinaryReaderError(
        `64-bit value 0x${hi.toString(16)}${lo
          .toString(16)
          .padStart(8, "0")} exceeds Number.MAX_SAFE_INTEGER`,
      );
    }
    return hi * 0x1_0000_0000 + lo;
  }

  readF32(): number {
    this.ensure(4);
    const v = this.view.getFloat32(this.offset, false);
    this.offset += 4;
    return v;
  }

  readDouble(): number {
    this.ensure(8);
    const v = this.view.getFloat64(this.offset, false);
    this.offset += 8;
    return v;
  }

  // -- Bytes / strings --------------------------------------------------

  /**
   * Returns a *view* into the underlying buffer (no copy).  Callers must not
   * mutate the result.  Use `.slice()` if you need an independent copy.
   */
  readBytes(n: number): Uint8Array {
    this.ensure(n);
    const out = this.bytes.subarray(this.offset, this.offset + n);
    this.offset += n;
    return out;
  }

  /** Fixed-length ASCII string (no length prefix, no terminator). */
  readAscii(n: number): string {
    this.ensure(n);
    let s = "";
    for (let i = 0; i < n; i++) {
      s += String.fromCharCode(this.bytes[this.offset + i]!);
    }
    this.offset += n;
    return s;
  }

  /**
   * PSD-style Pascal string: 1-byte length, that many bytes of MacRoman-ish
   * text, then padded with zeros so the *total* byte count (length byte +
   * content) is a multiple of `pad`.
   *
   * The legacy layer-name field uses `pad = 4`.  Resource block names use
   * `pad = 2`.  Some sub-records use `pad = 1` (no padding).
   */
  readPascalString(pad: 1 | 2 | 4 = 1): string {
    const len = this.readU8();
    let s = "";
    for (let i = 0; i < len; i++) {
      s += String.fromCharCode(this.readU8());
    }
    const consumed = 1 + len;
    const overshoot = consumed % pad;
    if (overshoot !== 0) {
      this.skip(pad - overshoot);
    }
    return s;
  }

  /**
   * Unicode string as written by Photoshop: 4-byte character count followed
   * by that many UTF-16 BE code units.  No null terminator is consumed (the
   * spec is inconsistent here — Photoshop sometimes includes a trailing
   * NUL inside the count, which we keep verbatim).
   */
  readUnicodeString(): string {
    const length = this.readU32();
    this.ensure(length * 2);
    let s = "";
    for (let i = 0; i < length; i++) {
      const code =
        (this.bytes[this.offset]! << 8) | this.bytes[this.offset + 1]!;
      this.offset += 2;
      s += String.fromCharCode(code);
    }
    return s;
  }
}
